import React from 'react';
import { Star, Clock, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Badge, Button, Card } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import { formatPoints, formatRelativeTime } from '@/shared/utils/format';
import type { Chore } from '@/entities/chore';
import type { ChoreCompletion } from '@/entities/chore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface ChoreCardProps {
  chore: Chore;
  completions: ChoreCompletion[];
  userRole: 'parent' | 'child';
  onMarkComplete: () => void;
  onApprove: () => void;
  onReject: () => void;
}

// ---------------------------------------------------------------------------
// Status badge helper
// ---------------------------------------------------------------------------
function StatusBadge({ status }: { status: ChoreCompletion['status'] }): React.ReactElement {
  const variantMap = {
    pending: 'warning',
    approved: 'success',
    rejected: 'danger',
  } as const;

  return (
    <Badge variant={variantMap[status]}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// ChoreCard component
// ---------------------------------------------------------------------------
export default function ChoreCard({
  chore,
  completions,
  userRole,
  onMarkComplete,
  onApprove,
  onReject,
}: ChoreCardProps): React.ReactElement {
  const timesPerPeriod = chore.schedule?.timesPerPeriod ?? 1;
  const nonRejected = completions.filter((c) => c.status !== 'rejected');
  const pending = completions.filter((c) => c.status === 'pending');
  const approved = completions.filter((c) => c.status === 'approved');
  const mostRecentPending = pending[0] ?? null;

  // ---- Parent view ----
  if (userRole === 'parent') {
    return (
      <Card className="flex flex-col gap-3">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-slate-900">{chore.name}</h3>
            {chore.description && (
              <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{chore.description}</p>
            )}
          </div>
          <Badge variant="info" className="shrink-0">
            <Star size={11} aria-hidden="true" />
            {formatPoints(chore.pointValue)}
          </Badge>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
          {chore.assignedTo !== 'any' && (
            <span className="flex items-center gap-1">
              Assigned to:
              <span className="font-medium text-slate-700">{chore.assignedTo}</span>
            </span>
          )}
          {chore.category && (
            <Badge variant="neutral">{chore.category}</Badge>
          )}
          {timesPerPeriod > 1 && (
            <Badge variant="neutral">
              {approved.length}/{timesPerPeriod}×
            </Badge>
          )}
        </div>

        {/* Completion section */}
        {mostRecentPending ? (
          <div className="mt-1 border-t border-slate-100 pt-3">
            <div className="mb-3 flex items-center gap-1.5 text-xs text-warning-700">
              <Clock size={13} aria-hidden="true" />
              <span>
                {mostRecentPending.childName} submitted {formatRelativeTime(mostRecentPending.submittedAt)}
                {pending.length > 1 && ` (+${pending.length - 1} more)`}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                className="flex-1 bg-secondary-600 hover:bg-secondary-700 focus-visible:ring-secondary-500"
                onClick={onApprove}
                aria-label={`Approve ${chore.name} for ${mostRecentPending.childName}`}
              >
                <CheckCircle2 size={14} aria-hidden="true" />
                Approve
              </Button>
              <Button
                variant="danger"
                size="sm"
                className="flex-1"
                onClick={onReject}
                aria-label={`Reject ${chore.name} for ${mostRecentPending.childName}`}
              >
                <XCircle size={14} aria-hidden="true" />
                Reject
              </Button>
            </div>
          </div>
        ) : nonRejected.length > 0 ? (
          <div className="mt-1 border-t border-slate-100 pt-3 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {nonRejected[0].childName} • {formatRelativeTime(nonRejected[0].submittedAt)}
            </span>
            <StatusBadge status={nonRejected[0].status} />
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">Not yet submitted</p>
        )}
      </Card>
    );
  }

  // ---- Child view ----
  const canSubmitMore = nonRejected.length < timesPerPeriod;
  const allDone = nonRejected.length >= timesPerPeriod;
  const allApproved = approved.length >= timesPerPeriod;
  const hasPending = pending.length > 0;

  return (
    <Card
      className={cn(
        'flex flex-col gap-4 transition-all duration-200',
        allApproved && 'border-secondary-300 bg-secondary-50/30',
      )}
    >
      {/* Chore name + points */}
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-bold text-slate-900 leading-tight">{chore.name}</h3>
        <div
          className="flex shrink-0 items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-sm font-bold text-accent-700"
          aria-label={`${chore.pointValue} points`}
        >
          <Star size={14} className="text-accent-500" aria-hidden="true" />
          {chore.pointValue}
        </div>
      </div>

      {chore.description && (
        <p className="text-sm text-slate-600">{chore.description}</p>
      )}

      {/* Progress dots for multi-completion chores */}
      {timesPerPeriod > 1 && (
        <div className="flex items-center gap-2" aria-label={`${nonRejected.length} of ${timesPerPeriod} done`}>
          {Array.from({ length: timesPerPeriod }).map((_, i) => {
            const isApproved = i < approved.length;
            const isPendingDot = !isApproved && i < nonRejected.length;
            return (
              <div
                key={i}
                className={cn(
                  'h-3 w-3 rounded-full border-2 transition-colors',
                  isApproved && 'border-secondary-500 bg-secondary-500',
                  isPendingDot && 'border-warning-400 bg-warning-200',
                  !isApproved && !isPendingDot && 'border-slate-300 bg-white',
                )}
              />
            );
          })}
          <span className="ml-1 text-xs text-slate-500">
            {approved.length}/{timesPerPeriod} approved
          </span>
        </div>
      )}

      {/* Action / status area */}
      {canSubmitMore && !hasPending && (
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={onMarkComplete}
          className="min-h-[52px] text-base"
          aria-label={`Mark ${chore.name} as done`}
        >
          <CheckCircle2 size={20} aria-hidden="true" />
          {timesPerPeriod > 1 && nonRejected.length > 0
            ? `Mark Done Again (${nonRejected.length + 1}/${timesPerPeriod})`
            : 'Mark as Done! ✓'}
        </Button>
      )}

      {canSubmitMore && hasPending && (
        <div className="space-y-2">
          <div className="flex items-center justify-center gap-2 rounded-xl bg-warning-50 px-4 py-3 text-sm font-medium text-warning-700">
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            <span>Waiting for approval ({pending.length} pending)...</span>
          </div>
          {/* Allow submitting more if quota not yet reached */}
        </div>
      )}

      {allDone && !allApproved && (
        <div className="flex items-center justify-center gap-2 rounded-xl bg-warning-50 px-4 py-3 text-sm font-medium text-warning-700">
          <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          <span>All submitted — waiting for approval...</span>
        </div>
      )}

      {allApproved && (
        <div
          className="flex items-center justify-center gap-2 rounded-xl bg-secondary-100 px-4 py-3 text-sm font-bold text-secondary-700"
          role="status"
          aria-label={`Approved! You earned ${approved.reduce((s, c) => s + c.pointValue, 0)} points`}
        >
          <CheckCircle2 size={18} aria-hidden="true" />
          {timesPerPeriod > 1
            ? `All done! +${approved.reduce((s, c) => s + c.pointValue, 0)} pts`
            : `Approved! +${approved[0]?.pointValue ?? chore.pointValue} pts`}
        </div>
      )}

      {/* Show rejection note for the most recent rejected completion */}
      {!canSubmitMore && !allApproved && completions.some((c) => c.status === 'rejected') && (
        <div
          className="rounded-xl bg-danger-50 px-4 py-3"
          role="status"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-danger-700">
            <XCircle size={16} aria-hidden="true" />
            Not approved
          </div>
          {completions.find((c) => c.status === 'rejected' && c.rejectionNote)?.rejectionNote && (
            <p className="mt-1.5 text-xs text-danger-600 leading-relaxed">
              "{completions.find((c) => c.status === 'rejected')!.rejectionNote}"
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
